import type { Activity as ActivityType } from '@/repositories';

type ConvertAmountFn = (amount: number, fromCoinType?: string) => number;

export const BIG_WIN_RULES = {
	majorProfitThreshold: 100,
	minorProfitThreshold: 50,
	minorMultiplierThreshold: 10,
} as const;

export const HUGE_WIN_STORAGE_KEY = 'suigar.events.huge-wins.dismiss';

export const getActivityId = (activity: ActivityType): string => activity.txId || activity.betId || '';

export function filterPendingActivities<T extends { betId?: string | null; txId?: string | null }>(
	activities: T[] | null | undefined,
	pendingIds: Iterable<string> | null | undefined,
	options: { includeTxId?: boolean } = {},
): T[] {
	const { includeTxId = true } = options;
	const pendingSet = new Set(pendingIds ?? []);
	if (!activities) return [];

	return activities.filter((activity) => {
		const betId = activity?.betId;
		if (betId && pendingSet.has(betId)) {
			return false;
		}

		if (includeTxId) {
			const txId = (activity as any)?.txId;
			if (txId && pendingSet.has(txId)) {
				return false;
			}
		}

		return true;
	});
}

export const isBigWinActivity = (
	activity: ActivityType,
	convertAmount: ConvertAmountFn,
	criteria: {
		majorProfitThreshold?: number;
		minorProfitThreshold?: number;
		minorMultiplierThreshold?: number;
	} = BIG_WIN_RULES,
) => {
	if (!activity?.win) return false;

	const coinType = activity.coin_type || '0x2::sui::SUI';
	const stake = Number(activity.stake ?? 0);
	const reward = Number(activity.reward ?? 0);
	const profit = convertAmount(reward - stake, coinType);
	const multiplier = Number(activity.payout_multiplier ?? 0);

	const major = criteria.majorProfitThreshold ?? BIG_WIN_RULES.majorProfitThreshold;
	const minor = criteria.minorProfitThreshold ?? BIG_WIN_RULES.minorProfitThreshold;
	const minMult = criteria.minorMultiplierThreshold ?? BIG_WIN_RULES.minorMultiplierThreshold;

	return profit >= major || (profit >= minor && multiplier >= minMult);
};
